import type * as React from "react";
import { type ReactNode, useCallback, useEffect, useMemo, useState } from "react";
import { Button, MenuItem, styled, TextField, type TextFieldProps } from "~/common/mui";
import { C_DROPDOWN_HOVER } from "../icon/Colors";
import { toMapFn } from "~/common/utils/reducers";
import Checkbox from "../Checkbox";
import { useTranslation } from "react-i18next";
import { type ItemDef } from "~/common/types";

export interface SimpleMultiSelectProps extends Omit<TextFieldProps<"outlined">, "variant"> {
  values: ItemDef[];
  value: string[] | number[];
  renderValue?: (value: unknown) => React.ReactNode;
  label?: string;
  children?: React.ReactNode;
  fullWidth?: boolean;
  "data-test-id"?: string;
}

const SimpleMultiSelectItem = styled(MenuItem)({
  "&.Mui-selected": {
    backgroundColor: "transparent !important",
  },
  "&.Mui-focusVisible": {
    backgroundColor: `${C_DROPDOWN_HOVER} !important`,
  },
});

export const SimpleMultiSelect = (props: SimpleMultiSelectProps) => {
  const { values, children, onChange, value, ...rest } = props;
  const { t } = useTranslation();

  const [internalValue, setInternalValue] = useState(value);

  useEffect(() => setInternalValue(value), [value]);

  // Component does not provide a better type...
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const internalOnChange = useCallback((e: any) => setInternalValue(e.target.value), []);

  const [isOpen, setOpen] = useState(false);

  const elements = useMemo(() => {
    return createMultiselectItems(values, internalValue as string[], rest["data-test-id"]);
  }, [values, internalValue, rest]);

  const defaultRenderValue = useMemo(() => {
    const labelMap = toMapFn(
      values,
      (item) => item.code,
      (item) => item.label,
    );
    return (value: unknown) => {
      return (value as string[]).map((v) => labelMap[v]).join(", ") as ReactNode;
    };
  }, [values]);

  const onSave = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      // TODO - check after m-ui update
      // @ts-ignore
      onChange?.({ target: { value: [...internalValue], name: "" } }, null);
      setOpen(false);
    },
    [internalValue, onChange],
  );

  const onClose = useCallback(() => {
    setInternalValue(value);
    setOpen(false);
  }, [value]);

  const onOpen = useCallback(() => {
    setOpen(true);
  }, []);

  return (
    <TextField
      {...rest}
      select
      slotProps={{
        select: {
          multiple: true,
          onClose,
          onOpen,
          open: isOpen,
          renderValue: rest.renderValue ?? defaultRenderValue
        },
      }}
      value={internalValue}
      onChange={internalOnChange}
      variant={"outlined"}
    >
      {elements}
      <div className={"p-3"}>
        <Button onClick={onSave} fullWidth={true}>
          {t("generic.save")}
        </Button>
      </div>
      {children}
    </TextField>
  );
};

export const createMultiselectItems = (items: ItemDef[], values: string[], testId?: string) => {
  const checkedMap = toMapFn(
    values,
    (key) => key,
    () => true,
  );
  return items.map((item) => {
    const childId = testId ? `${testId}-item` : undefined;
    const active = !!checkedMap[item.code];
    return (
      <SimpleMultiSelectItem
        key={item.code}
        className={"d-flex"}
        value={item.code}
        data-test-id={childId}
        disabled={item.disabled && !active}
      >
        <Checkbox checked={active} />
        <span className={"icon-margin-l"}>{item.label}</span>
      </SimpleMultiSelectItem>
    );
  });
};
